import { useSeasonTable } from '@/context/LivescoreSeasonTableContext';
import { useEffect, useState } from 'react';
import LivescoreSeasonTableTypePicker from './subcomponents/LivescoreSeasonTableTypePicker';
import { cn } from '@/utils/cn';
import Image from 'next/image';
import { ApiLeagueLeagueIdGet200ResponseDataSeasonTableStandingsTotalInnerStandingsInner } from '@/api/livescore/generated';
import LivescoreSeasonTableSkeleton from './subcomponents/LivescoreSeasonTableSkeleton';
import LivescoreEntityLink from '@/components/atoms/LivescoreEntityLink/LivescoreEntityLink';

interface LivescoreSeasonTableElementProps {
  last?: boolean;
  competitor: ApiLeagueLeagueIdGet200ResponseDataSeasonTableStandingsTotalInnerStandingsInner;
  form?: boolean;
  color?: string;
  score?: LivescoreSeasonTableProps['score'];
  highlight?: boolean;
  isAhref?: boolean;
}

const LivescoreSeasonTableElement = ({
  last,
  competitor,
  form,
  color,
  score,
  highlight,
  isAhref,
}: LivescoreSeasonTableElementProps) => (
  <tr className="sm:text-[12px] text-[9px] group text-black dark:text-white">
    <td
      className={cn(
        'md:pl4 group-odd:dark:bg-black-400 group-odd:bg-white_theme-50 py-2.5 md:w-12 w-9 flex justify-center',
        last && 'rounded-bl-3xl',
        highlight && 'dark:dark:!bg-green-600 !bg-primary-150',
      )}
    >
      <span
        style={color ? { backgroundColor: color } : {}}
        className={cn(
          '!text-white rounded-full w-[21px] h-[21px] flex items-center justify-center font-bold sm:text-[9px] text-[7px]',
          !color && 'dark:bg-gray-650 bg-white_theme-100 dark:!text-white !text-black',
        )}
      >
        <span className="ml-0.5 sm:mt-0.5">{competitor.rank}.</span>
      </span>
    </td>
    <td
      className={cn(
        'w-auto md:pl-4 pl-2 group-odd:dark:bg-black-400 group-odd:bg-white_theme-50',
        highlight && 'dark:!bg-green-600 !bg-primary-150',
      )}
    >
      <LivescoreEntityLink
        href={`/livescore/team/${competitor.competitor.slug ?? competitor.competitor.id}`}
        className={cn('flex items-center gap-2 py-2.5', highlight && 'dark:!bg-green-600 !bg-primary-150')}
        isAhref={isAhref}
      >
        {competitor.competitor.logo_url ? (
          <div className="w-4 h-4 relative">
            <Image
              src={competitor.competitor.logo_url}
              alt={competitor.competitor.name}
              layout="fill"
              objectFit="contain"
            />
          </div>
        ) : (
          <span className="bg-primary-600 rounded-full w-4 h-4 flex items-center justify-center text-[6px]">
            {competitor.competitor.abbreviation}
          </span>
        )}
        <span className="font-semibold !line-clamp-1 overflow-ellipsis">{competitor.competitor.name}</span>
      </LivescoreEntityLink>
    </td>
    <td
      className={cn(
        'sm:w-12 w-8 group-odd:dark:bg-black-400 group-odd:bg-white_theme-50',
        highlight && 'dark:!bg-green-600 !bg-primary-150',
      )}
    >
      {score && highlight && (
        <span className="dark:text-black text-white font-gameplay sm:px-2 px-1 pb-0.5 pt-[3px] bg-primary-600 sm:text-xxs text-[7px] rounded-[2.66px]">
          {score.home.score}:{score.away.score}
        </span>
      )}
    </td>
    {form ? (
      <td
        className={cn(
          'text-center group-odd:dark:bg-black-400 group-odd:bg-white_theme-50 py-2.5 w-14 pr-6',
          last && 'rounded-br-3xl',
          highlight && 'dark:!bg-green-600 !bg-primary-150',
        )}
      >
        <div className="text-white flex gap-2 float-right">
          {competitor.competitor.form ? (
            competitor.competitor.form
              .split('')
              .map((result) =>
                result === 'W' ? (
                  <span className="sm:w-5 sm:h-5 w-3.5 h-3.5 rounded-[2.66px]  bg-[#06C000] flex items-center justify-center font-bold sm:text-xxs text-[8px]">
                    Z
                  </span>
                ) : result === 'D' ? (
                  <span className="sm:w-5 sm:h-5 w-3.5 h-3.5 rounded-[2.66px]  bg-[#FFA600] flex items-center justify-center font-bold sm:text-xxs text-[8px]">
                    R
                  </span>
                ) : (
                  <span className="sm:w-5 sm:h-5 w-3.5 h-3.5 rounded-[2.66px]  bg-[#FF0004] flex items-center justify-center font-bold sm:text-xxs text-[8px]">
                    P
                  </span>
                ),
              )
          ) : (
            <p className="text-xs text-gray-400">Brak informacji</p>
          )}
        </div>
      </td>
    ) : (
      <>
        <td
          className={cn(
            'text-center group-odd:dark:bg-black-400 group-odd:bg-white_theme-50 py-2.5 sm:w-8 w-6',
            highlight && 'dark:!bg-green-600 !bg-primary-150',
          )}
        >
          {competitor.played}
        </td>
        <td
          className={cn(
            'text-center group-odd:dark:bg-black-400 group-odd:bg-white_theme-50 py-2.5 sm:w-16 w-12',
            highlight && 'dark:!bg-green-600 !bg-primary-150',
          )}
        >
          {competitor.goals.for}:{competitor.goals.against}
        </td>
        <td
          className={cn(
            'text-center group-odd:dark:bg-black-400 group-odd:bg-white_theme-50 py-2.5 sm:w-14 w-10 pr-6',
            last && 'rounded-br-3xl',
            highlight && 'dark:!bg-green-600 !bg-primary-150',
          )}
        >
          {competitor.points}
        </td>
      </>
    )}
  </tr>
);

interface LivescoreSeasonTableProps {
  score?: {
    home: {
      id: string;
      score?: number;
    };
    away: {
      id: string;
      score?: number;
    };
  };
}

const LivescoreSeasonTable = ({ score }: LivescoreSeasonTableProps) => {
  const { seasonTable, fetchSeasonTable, loading, type, showForm, outcomesUp, outcomesDown, seasonId, season } =
    useSeasonTable();
  const [lastSeasonId, setLastSeasonId] = useState<string | null>(seasonId);

  useEffect(() => {
    if (!seasonTable || seasonId !== lastSeasonId) {
      fetchSeasonTable();
      setLastSeasonId(seasonId);
    }
  }, [seasonTable, seasonId]);

  if (loading) {
    return <LivescoreSeasonTableSkeleton />;
  }

  if (!seasonTable) {
    return <p className="text-xs text-gray-400">Brak informacji</p>;
  }

  return (
    <>
      <div className="w-full border dark:border-border-400 border-gray-250 rounded-t-3xl rounded-b-xl">
        <>
          <div className="py-2 px-3">
            <div className="space-y-2">
              <LivescoreSeasonTableTypePicker />
            </div>
          </div>
          <div className="mt-2">
            <table className="w-full">
              {seasonTable?.[type].map((group, index) => (
                <>
                  <thead className="border-b dark:border-border-400 border-gray-250">
                    <tr className="md:text-xxs text-[8px] text-gray-400">
                      <th className="text-center py-3 sm:w-12 w-9 sm:pl-4">#</th>
                      <th className="text-left py-3 w-auto sm:pl-4 pl-2 !line-clamp-1 overflow">
                        <span className="!line-clamp-1 overflow-ellipsis">
                          {seasonTable?.[type] && seasonTable?.[type].length > 1 ? group.name : 'DRUŻYNA'}
                        </span>
                      </th>
                      <th className="sm:w-12 w-8">{/* This th is for score */}</th>
                      {showForm ? (
                        <th className="text-center py-3 w-[132px]">
                          OSTATNIE {group.standings[0].competitor.form?.length} MECZ
                          {group.standings[0].competitor.form?.length && group.standings[0].competitor.form?.length < 5
                            ? 'E'
                            : 'ÓW'}
                        </th>
                      ) : (
                        <>
                          <th className="text-center py-3 sm:w-8 w-6">M</th>
                          <th className="text-center py-3 sm:w-16 w-12">B</th>
                          <th className="text-center py-3 sm:w-14 w-10 pr-6">P</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {group.standings.map((team, index) => (
                      <LivescoreSeasonTableElement
                        key={index}
                        competitor={team}
                        last={index === group.standings.length - 1}
                        form={showForm}
                        color={
                          team.outcome ? outcomesUp.get(team.outcome) ?? outcomesDown.get(team.outcome) : undefined
                        }
                        score={score}
                        highlight={[score?.away.id, score?.home.id].includes(team.competitor.id)}
                        isAhref={season?.seoLinks}
                      />
                    ))}
                  </tbody>
                </>
              ))}
            </table>
          </div>
        </>
      </div>
      <div className="text-black dark:text-white sm:mt-6 mt-4 mb-2 sm:mb-0">
        {outcomesUp.size > 0 && (
          <div className="space-y-2">
            {Array.from(outcomesUp.keys()).map((key) => (
              <div key={key} className="flex items-center gap-2">
                <div
                  className="w-3.5 h-3.5 rounded-full"
                  style={{ backgroundColor: outcomesUp.get(key) }}
                  aria-hidden
                />
                <span className="text-xxs">
                  Awans{' '}
                  {key !== 'Promotion' && key !== 'Awans' && `- ${key.replace('Promotion', '').replace('Awans', '')}`}
                </span>
              </div>
            ))}
          </div>
        )}
        {outcomesDown.size > 0 && (
          <div className="space-y-2 mt-2">
            {Array.from(outcomesDown.keys()).map((key) => (
              <div key={key} className="flex items-center gap-2">
                <div
                  className="w-3.5 h-3.5 rounded-full"
                  style={{ backgroundColor: outcomesDown.get(key) }}
                  aria-hidden
                />
                <span className="text-xxs">
                  Spadek{' '}
                  {key !== 'Relegation' &&
                    key !== 'Spadek' &&
                    `- ${key.replace('Relegation', '').replace('Spadek', '')}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default LivescoreSeasonTable;
